import { useEffect, useState } from 'react';
import { SNAP_LABELS, SNAP_VALUES } from '@/timing/snap';
import { snapTick } from '@/timing/snap';
import { barBeatToTicks, formatBarBeat, signatureAt, tempoEventAt, ticksToBarBeat } from '@/timing/tempoMap';
import { signatureMapOf, tempoMapOf } from '@/domain/selectors';
import { command } from '@/commands/registry';
import * as TransportCommands from '@/commands/transportCommands';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import { bareOverlaps } from '@/domain/overlap';
import { TransportReadout } from './TransportReadout';

/** Only power-of-two beat units are musically meaningful, and the command agrees. */
const BEAT_UNITS = [1, 2, 4, 8, 16, 32] as const;

export function TopBar(): JSX.Element {
  const project = useDaw((s) => s.project);
  const dirty = useDaw((s) => s.dirty);
  const transportState = useDaw((s) => s.transportState);
  const undoLabel = useDaw((s) => s.undoLabel);
  const redoLabel = useDaw((s) => s.redoLabel);
  const editTool = useDaw((s) => s.editTool);
  const hasRange = useDaw((s) => s.selection.range !== null && s.selection.range.endTicks - s.selection.range.startTicks >= 1);
  const hasLoop = project.transport.loopEndTicks - project.transport.loopStartTicks >= 1;
  const rippleEditing = useDaw((s) => s.rippleEditing);
  const autoFadeOverlaps = useDaw((s) => s.autoFadeOverlaps);
  // Counted from the project rather than held in state: it is a fact about the
  // clips, and anything else would be a second copy of it to keep in step.
  const overlapCount = useDaw((s) => bareOverlaps(s.project).length);
  const clipGainEditing = useDaw((s) => s.clipGainEditing);
  const dispatch = useDaw((s) => s.dispatch);

  const [tempoDraft, setTempoDraft] = useState<string | null>(null);
  const [sigDraft, setSigDraft] = useState<string | null>(null);
  // Both the tempo and the meter fields act on the event in force where the
  // playhead is, so one pair of controls serves a project with a flat tempo and
  // a project with an accelerando in it.
  const tempoMap = tempoMapOf(project);
  const tempo = tempoEventAt(tempoMap, project.transport.playheadTicks);
  const bpm = tempo.event.bpm;
  const tempoChangeHere = tempo.event.tick > 0;

  // The meter fields edit the signature in force where the playhead is, so the
  // same two controls serve a project with one meter and a project with twenty.
  const sigMap = signatureMapOf(project);
  const playheadBar = ticksToBarBeat(sigMap, project.transport.playheadTicks).bar;
  const barTick = barBeatToTicks(sigMap, playheadBar);
  const signature = signatureAt(sigMap, project.transport.playheadTicks);
  const changeStartsHere = signature.tick === barTick && barTick > 0;

  useEffect(() => {
    setTempoDraft(null);
  }, [bpm]);

  useEffect(() => {
    setSigDraft(null);
  }, [signature.numerator, signature.denominator]);

  const setSignature = (numerator: number, denominator: number, tick = signature.tick): void => {
    dispatch(command(TransportCommands.setTimeSignature, { tick, numerator, denominator }));
  };

  // Adding a change starts it at the meter already in force, so the button is
  // an "and from here it can differ" rather than a jump to some default.
  const addChangeHere = (): void => {
    setSignature(signature.numerator, signature.denominator, barTick);
    getServices().commands.breakCoalescing();
  };

  const removeChangeHere = (): void => {
    dispatch(command(TransportCommands.removeTimeSignature, { tick: barTick }));
    getServices().commands.breakCoalescing();
  };

  // The draft is kept as text so a half-typed number is not clamped under the
  // user's fingers; it is only turned into a command when the field is left.
  const commitNumerator = (): void => {
    if (sigDraft === null) return;
    const value = Number.parseInt(sigDraft, 10);
    if (Number.isFinite(value)) setSignature(Math.min(32, Math.max(1, value)), signature.denominator);
    setSigDraft(null);
  };

  const commitTempo = (): void => {
    if (tempoDraft === null) return;
    const value = Number.parseFloat(tempoDraft);
    if (Number.isFinite(value)) useDaw.getState().setTempo(Math.min(900, Math.max(5, value)), tempo.event.tick);
    setTempoDraft(null);
  };

  // A tempo change lands where the playhead is, snapped by the user's own grid
  // setting rather than forced onto bar lines: a ritardando often starts on a
  // beat, and the model has no reason to disagree.
  const tempoInsertTick = (): number =>
    snapTick(sigMap, project.transport.snap, project.transport.playheadTicks);

  const addTempoChange = (): void => {
    useDaw.getState().setTempo(bpm, Math.max(1, tempoInsertTick()));
    getServices().commands.breakCoalescing();
  };

  const removeTempoChange = (): void => {
    dispatch(command(TransportCommands.removeTempo, { tick: tempo.event.tick }));
    getServices().commands.breakCoalescing();
  };

  const toggleTempoRamp = (): void => {
    dispatch(command(TransportCommands.setTempoRamp, { tick: tempo.event.tick, ramp: !tempo.ramps, shape: 'linear' as const }));
    getServices().commands.breakCoalescing();
  };

  return (
    <header className="topbar">
      <div className="topbar__brand">
        <span className="topbar__mark">Stage Zero</span>
        <span className="topbar__local">local-first</span>
      </div>

      <button className="btn" onClick={() => void useDaw.getState().setModal('projects')}>
        Projects
      </button>
      <button className="btn" onClick={() => void useDaw.getState().saveProject()} title="Save project (Cmd/Ctrl+S)">
        Save
      </button>

      <input
        className="project-name"
        value={project.meta.name}
        aria-label="Project name"
        onChange={(e) => dispatch(command(TransportCommands.renameProject, { name: e.target.value || 'Untitled' }))}
      />
      {dirty && <span className="dirty-dot" title="Unsaved changes" />}

      <div className="topbar__spacer" />

      <div className="group" role="group" aria-label="Transport">
        <button
          className={`btn btn--icon ${transportState === 'playing' ? 'btn--active' : ''}`}
          onClick={() => useDaw.getState().togglePlay()}
          title="Play / pause (Space)"
          aria-label="Play or pause"
        >
          {transportState === 'playing' ? '❚❚' : '▶'}
        </button>
        <button
          className="btn btn--icon"
          onClick={() => useDaw.getState().stop()}
          title="Stop and return to the playhead position"
          aria-label="Stop"
        >
          ■
        </button>
        <button
          className={`btn btn--icon btn--record ${transportState === 'recording' ? 'btn--active' : ''}`}
          onClick={() => void useDaw.getState().toggleRecording()}
          title="Record on armed tracks (R)"
          aria-label="Record"
        >
          ●
        </button>
      </div>

      <TransportReadout />

      <div className="field">
        <span className="field__label">BPM</span>
        <input
          className="numeric"
          style={{ width: 46 }}
          value={tempoDraft ?? bpm.toFixed(bpm % 1 === 0 ? 0 : 2)}
          aria-label="Tempo in beats per minute"
          onChange={(e) => setTempoDraft(e.target.value)}
          onBlur={commitTempo}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setTempoDraft(null);
          }}
        />
        <button
          className={`btn btn--ghost btn--icon ${tempo.ramps ? 'btn--active' : ''}`}
          aria-label={tempo.ramps ? 'Hold this tempo' : 'Ramp to the next tempo change'}
          aria-pressed={tempo.ramps}
          title={
            tempo.ramps
              ? 'Ramping to the next tempo change. Click to hold this tempo instead.'
              : 'Hold this tempo until the next change. Click to ramp towards it instead.'
          }
          onClick={toggleTempoRamp}
        >
          {tempo.ramps ? '\u2197' : '\u2192'}
        </button>
        <button
          className={`btn btn--ghost btn--icon ${tempoChangeHere ? 'btn--active' : ''}`}
          disabled={!tempoChangeHere && tempoInsertTick() <= 0}
          aria-label={tempoChangeHere ? 'Remove this tempo change' : 'Add a tempo change at the playhead'}
          title={
            tempoChangeHere
              ? `Remove the tempo change at ${formatBarBeat(sigMap, tempo.event.tick)}`
              : tempoInsertTick() <= 0
                ? 'The tempo at the start of the project is its base tempo; move the playhead to add a change.'
                : `Add a tempo change at ${formatBarBeat(sigMap, tempoInsertTick())}`
          }
          onClick={tempoChangeHere ? removeTempoChange : addTempoChange}
        >
          {tempoChangeHere ? '\u2212' : '+'}
        </button>
      </div>

      <div className="field">
        <span className="field__label">Sig</span>
        <input
          className="numeric"
          style={{ width: 26 }}
          type="number"
          min={1}
          max={32}
          step={1}
          value={sigDraft ?? String(signature.numerator)}
          aria-label="Time signature beats per bar"
          title="Beats per bar. The grid, the bar numbers and snapping all follow it."
          onChange={(e) => setSigDraft(e.target.value)}
          onBlur={commitNumerator}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setSigDraft(null);
          }}
        />
        <span className="field__sep" aria-hidden="true">
          /
        </span>
        <select
          className="numeric"
          style={{ width: 34, cursor: 'pointer' }}
          value={signature.denominator}
          aria-label="Time signature beat unit"
          onChange={(e) => setSignature(signature.numerator, Number(e.target.value))}
        >
          {BEAT_UNITS.map((unit) => (
            <option key={unit} value={unit}>
              {unit}
            </option>
          ))}
        </select>
        <button
          className={`btn btn--ghost btn--icon ${changeStartsHere ? 'btn--active' : ''}`}
          disabled={barTick === 0}
          aria-label={changeStartsHere ? 'Remove the meter change at this bar' : 'Add a meter change at this bar'}
          title={
            barTick === 0
              ? 'Bar 1 carries the project meter; put the playhead on a later bar to add a change.'
              : changeStartsHere
                ? `Remove the meter change at bar ${playheadBar}`
                : `Add a meter change at bar ${playheadBar}`
          }
          onClick={changeStartsHere ? removeChangeHere : addChangeHere}
        >
          {changeStartsHere ? '\u2212' : '+'}
        </button>
      </div>

      <button
        className={`btn btn--ghost ${rippleEditing ? 'btn--active' : ''}`}
        aria-pressed={rippleEditing}
        aria-label="Ripple editing"
        title="With ripple on, deleting, trimming or dragging a clip moves what follows it on its track, and the automation moves too."
        onClick={() => useDaw.getState().setRippleEditing(!rippleEditing)}
      >
        Ripple
      </button>

      <button
        className={`btn btn--ghost ${autoFadeOverlaps ? 'btn--active' : ''}`}
        aria-pressed={autoFadeOverlaps}
        aria-label="Fade overlaps automatically"
        title={
          'With this on, an edit that leaves two clips on top of each other crossfades them across the whole ' +
          'overlap. Overlaps are marked on the timeline either way; this decides whether they are fixed for you.'
        }
        onClick={() => useDaw.getState().setAutoFadeOverlaps(!autoFadeOverlaps)}
      >
        Auto-fade
      </button>

      {overlapCount > 0 && (
        <button
          className="btn btn--ghost"
          aria-label={`Crossfade ${overlapCount} overlapping join(s)`}
          title={
            `${overlapCount} place(s) where two clips play at once. Crossfade each across the whole overlap, ` +
            'so nothing is heard twice.'
          }
          onClick={() => useDaw.getState().fadeOverlaps()}
        >
          Fix {overlapCount} overlap{overlapCount === 1 ? '' : 's'}
        </button>
      )}

      <button
        className={`btn btn--ghost ${clipGainEditing ? 'btn--active' : ''}`}
        aria-pressed={clipGainEditing}
        aria-label="Clip gain"
        title="Draw a level ride inside a clip: click to add a point, drag to move one, alt-click to remove it."
        onClick={() => useDaw.getState().setClipGainEditing(!clipGainEditing)}
      >
        Clip gain
      </button>

      <div className="field">
        <span className="field__label">Snap</span>
        <select
          className="numeric"
          style={{ width: 54, cursor: 'pointer' }}
          value={project.transport.snap}
          aria-label="Grid snap"
          onChange={(e) => useDaw.getState().setSnap(e.target.value as (typeof SNAP_VALUES)[number])}
        >
          {SNAP_VALUES.map((value) => (
            <option key={value} value={value}>
              {SNAP_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="field__label">Xfade</span>
        <input
          className="numeric"
          style={{ width: 38 }}
          type="number"
          min={0}
          max={2000}
          step={5}
          value={Math.round(project.transport.crossfadeSec * 1000)}
          aria-label="Crossfade length in milliseconds"
          title="Length used for new crossfades and at comp boundaries. Zero turns them off."
          onChange={(e) => useDaw.getState().setDefaultCrossfade(Math.max(0, Number(e.target.value)) / 1000)}
          onBlur={() => getServices().commands.breakCoalescing()}
        />
      </div>

      <div className="group" role="group" aria-label="Tools">
        <button
          className={`btn ${editTool === 'select' ? 'btn--active' : ''}`}
          onClick={() => useDaw.getState().setEditTool('select')}
          title="Select and edit clips"
        >
          Select
        </button>
        <button
          className={`btn ${editTool === 'split' ? 'btn--active' : ''}`}
          onClick={() => useDaw.getState().setEditTool('split')}
          title="Click a clip to split it (S splits at the playhead)"
        >
          Split
        </button>
        <button
          className={`btn ${editTool === 'range' ? 'btn--active' : ''}`}
          onClick={() => useDaw.getState().setEditTool('range')}
          title="Drag across tracks to mark a stretch of time to edit"
        >
          Range
        </button>
      </div>

      <RangeMenu />

      <button
        className="btn"
        onClick={() => useDaw.getState().crossfadeSelection()}
        title="Crossfade the selected pair, or the edit at the playhead (Cmd/Ctrl+F)"
      >
        Crossfade
      </button>

      <div className="group" role="group" aria-label="Loop">
        <button
          className={`btn ${project.transport.loopEnabled ? 'btn--active' : ''}`}
          onClick={() => useDaw.getState().toggleLoop()}
          title="Loop the marked region"
        >
          Loop
        </button>
        <button
          className="btn"
          disabled={!hasRange}
          onClick={() => useDaw.getState().loopFromRange()}
          aria-label="Loop the range"
          title={hasRange ? 'Loop the marked range, and turn the loop on' : 'Mark a range first, with the Range tool'}
        >
          ←Range
        </button>
        <button
          className="btn"
          disabled={!hasLoop}
          onClick={() => useDaw.getState().rangeFromLoop()}
          aria-label="Mark the loop as the range"
          title={hasLoop ? 'Mark the looped stretch as the range, on every track' : 'There is no loop region to mark'}
        >
          →Range
        </button>
      </div>
      <button
        className={`btn ${project.transport.metronomeEnabled ? 'btn--active' : ''}`}
        onClick={() => useDaw.getState().toggleMetronome()}
        title="Metronome"
      >
        Click
      </button>

      <div className="group" role="group" aria-label="History">
        <button
          className="btn"
          disabled={!undoLabel}
          onClick={() => useDaw.getState().undo()}
          title={undoLabel ? `Undo ${undoLabel}` : 'Nothing to undo'}
        >
          Undo
        </button>
        <button
          className="btn"
          disabled={!redoLabel}
          onClick={() => useDaw.getState().redo()}
          title={redoLabel ? `Redo ${redoLabel}` : 'Nothing to redo'}
        >
          Redo
        </button>
      </div>

      <button className="btn" onClick={() => useDaw.getState().setModal('export')}>
        Export
      </button>
    </header>
  );
}

/**
 * The range operations.
 *
 * They are one menu rather than five buttons because they are alternatives to
 * each other — a stretch of time is spliced out *or* cropped to *or* repeated —
 * and because the toolbar has more useful things to show at all times.
 */
function RangeMenu(): JSX.Element {
  const hasRange = useDaw((s) => s.selection.range !== null);

  return (
    <select
      className="select"
      style={{ width: 92 }}
      value=""
      disabled={!hasRange}
      aria-label="Range edit"
      title={hasRange ? 'Edit the selected stretch of time' : 'Mark a range with the Range tool first'}
      onChange={(e) => {
        const [kind, scope] = e.target.value.split(':');
        e.target.value = '';
        if (!kind) return;
        // Bouncing is not a pure edit: it renders audio, so it goes through its
        // own action rather than the command that the rest of this menu uses.
        if (kind === 'bounce') {
          void useDaw.getState().bounceRange({ keepSource: scope === 'keep' });
          return;
        }
        if (kind === 'consolidate') {
          void useDaw.getState().consolidateRange();
          return;
        }
        useDaw.getState().applyRangeEdit(kind as never, scope === 'all');
      }}
    >
      <option value="">Edit range…</option>
      <option value="split">Split at edges</option>
      <option value="delete">Clear (leave gap)</option>
      <option value="spliceOut">Splice out (close gap)</option>
      <option value="spliceOut:all">Splice out — all tracks</option>
      <option value="insert">Insert time</option>
      <option value="insert:all">Insert time — all tracks</option>
      <option value="duplicate">Duplicate</option>
      <option value="copy">Copy range</option>
      <option value="pasteReplace">Paste over range</option>
      <option value="pasteInsert">Paste and insert</option>
      <option value="crop">Crop project to range</option>
      <option value="bounce">Bounce to a new track</option>
      <option value="bounce:keep">Bounce, keeping the source</option>
      <option value="consolidate">Consolidate to one clip</option>
    </select>
  );
}
